/**
 * Types for Mileage Simulator
 * Defines all interfaces and types for the Travel Legacy Mileage Quotation System
 */

/**
 * Available mileage programs
 */
export enum MileageProgram {
  SMILES = 'SMILES',
  LATAM_PASS = 'LATAM_PASS',
  AZUL_FIDELIDADE = 'AZUL_FIDELIDADE',
  TAP_MILESGO = 'TAP_MILESGO',
  AMERICAN_AADVANTAGE = 'AMERICAN_AADVANTAGE',
  UNITED_MILEAGEPLUS = 'UNITED_MILEAGEPLUS',
  DELTA_SKYMILES = 'DELTA_SKYMILES',
  FLYING_BLUE = 'FLYING_BLUE',
  BRITISH_AIRWAYS_AVIOS = 'BRITISH_AIRWAYS_AVIOS',
  IBERIA_PLUS = 'IBERIA_PLUS',
  ALASKA_MILEAGE_PLAN = 'ALASKA_MILEAGE_PLAN',
  AIR_CANADA_AEROPLAN = 'AIR_CANADA_AEROPLAN',
  EMIRATES_SKYWARDS = 'EMIRATES_SKYWARDS',
  QATAR_PRIVILEGE_CLUB = 'QATAR_PRIVILEGE_CLUB',
  TURKISH_MILESSMILES = 'TURKISH_MILESSMILES',
  AVIANCA_LIFEMILES = 'AVIANCA_LIFEMILES',
  COPA_CONNECTMILES = 'COPA_CONNECTMILES',
  ETIHAD_GUEST = 'ETIHAD_GUEST',
  SINGAPORE_KRISFLYER = 'SINGAPORE_KRISFLYER',
  LUFTHANSA_MILES_MORE = 'LUFTHANSA_MILES_MORE',
  QANTAS_FREQUENT_FLYER = 'QANTAS_FREQUENT_FLYER',
  CATHAY_ASIA_MILES = 'CATHAY_ASIA_MILES',
  ANA_MILEAGE_CLUB = 'ANA_MILEAGE_CLUB',
  JAL_MILEAGE_BANK = 'JAL_MILEAGE_BANK',
  HAWAIIANMILES = 'HAWAIIANMILES',
}

/**
 * Payment methods available for calculation
 */
export enum PaymentMethod {
  PIX = 'PIX',
  CARD_1X = 'CARD_1X',
  CARD_INSTALLMENTS = 'CARD_INSTALLMENTS',
}

/**
 * Represents a single flight leg (one-way trip)
 */
export interface FlightLeg {
  /** Unique identifier for the leg */
  id: string;
  /** The mileage program used for this leg */
  program: MileageProgram;
  /** Number of miles required */
  miles: number;
  /** Cost per thousand miles in BRL */
  costPerThousand: number;
  /** Boarding/airport tax in BRL */
  boardingTax: number;
}

/**
 * Represents a single quote option for a flight leg
 * (Same as FlightLeg but used in multi-quote context)
 */
export type Quote = FlightLeg;

/**
 * A combination of quotes (outbound + optional return)
 */
export interface Combo {
  /** Unique identifier for the combo */
  id: string;
  /** Outbound quote */
  outbound: Quote;
  /** Return quote (optional) */
  return?: Quote;
  /** Total cost of this combo */
  totalCost: number;
  /** Savings compared to airline price */
  savings: number;
  /** Savings percentage */
  savingsPercentage: number;
  /** Whether this combo is viable (cheaper than airline) */
  isViable: boolean;
  /** Rank position (1 = best, 2 = second best, etc) */
  rank: number;
}

/**
 * Complete quotation with multiple quote options per leg
 */
export interface MileageQuotation {
  /** Reference price from airline in BRL */
  airlinePrice: number;
  /** Outbound flight leg */
  outbound: FlightLeg;
  /** Return flight leg (optional for one-way trips) */
  return?: FlightLeg;
  /** Whether this is a round-trip */
  isRoundTrip: boolean;
}

/**
 * Multi-quote quotation structure with arrays of options
 */
export interface MultiQuoteQuotation {
  /** Reference price from airline in BRL */
  airlinePrice: number;
  /** Array of outbound quote options */
  outboundQuotes: Quote[];
  /** Array of return quote options (empty if one-way) */
  returnQuotes: Quote[];
  /** Whether this is a round-trip */
  isRoundTrip: boolean;
}

/**
 * Cost breakdown for the mileage quotation
 */
export interface CostBreakdown {
  /** Cost for outbound leg */
  outboundCost: number;
  /** Cost for return leg (if exists) */
  returnCost: number;
  /** Total cost (sum of all legs) */
  totalCost: number;
  /** Savings compared to airline price */
  savings: number;
  /** Savings percentage */
  savingsPercentage: number;
}

/**
 * Profit calculation for a specific payment method
 */
export interface ProfitCalculation {
  /** Payment method used */
  paymentMethod: PaymentMethod;
  /** Number of installments (for CARD_INSTALLMENTS) */
  installments?: number;
  /** Amount charged to the customer */
  chargedAmount: number;
  /** Amount actually received after fees */
  receivedAmount: number;
  /** Fee percentage applied */
  feePercentage: number;
  /** Fee amount in BRL */
  feeAmount: number;
  /** Gross profit in BRL */
  grossProfit: number;
  /** Gross profit percentage */
  grossProfitPercentage: number;
  /** Whether this operation is profitable */
  isProfitable: boolean;
  /** Whether the charged amount exceeds airline price */
  exceedsAirlinePrice: boolean;
}

/**
 * Complete profit analysis with all payment methods
 */
export interface ProfitAnalysis {
  /** Cost breakdown */
  costs: CostBreakdown;
  /** Amount the consultant wants to charge */
  desiredChargeAmount: number;
  /** Profit calculation for PIX */
  pix: ProfitCalculation;
  /** Profit calculation for Card 1x */
  card1x: ProfitCalculation;
  /** Profit calculations for installment options (1x to 12x) */
  cardInstallments: ProfitCalculation[];
  /** Best option based on highest profit */
  bestOption: ProfitCalculation;
}

/**
 * Combo ranking result
 */
export interface ComboRanking {
  /** All generated combos sorted by cost (best first) */
  combos: Combo[];
  /** The best combo (lowest cost) */
  bestCombo: Combo | null;
  /** Whether any viable combo exists */
  hasViableOption: boolean;
}

/**
 * Simulator state management (legacy single quote)
 */
export interface SimulatorState {
  quotation: MileageQuotation;
  desiredChargeAmount: number;
  profitAnalysis: ProfitAnalysis | null;
}

/**
 * Multi-quote simulator state management
 */
export interface MultiQuoteSimulatorState {
  quotation: MultiQuoteQuotation;
  desiredChargeAmount: number;
  ranking: ComboRanking | null;
  selectedCombo: Combo | null;
  profitAnalysis: ProfitAnalysis | null;
}

/**
 * Constants for fee calculations
 */
export const PAYMENT_FEES = {
  PIX: 0,
  CARD_1X: 0.0299, // 2.99%
  CARD_INSTALLMENT_RATE: 0.0399, // 3.99% per installment
} as const;

/**
 * Mileage program display names
 */
export const MILEAGE_PROGRAM_LABELS: Record<MileageProgram, string> = {
  [MileageProgram.SMILES]: 'Smiles',
  [MileageProgram.LATAM_PASS]: 'LATAM Pass',
  [MileageProgram.AZUL_FIDELIDADE]: 'Azul Fidelidade',
  [MileageProgram.TAP_MILESGO]: 'TAP Miles&Go',
  [MileageProgram.AMERICAN_AADVANTAGE]: 'American Airlines AAdvantage',
  [MileageProgram.UNITED_MILEAGEPLUS]: 'United MileagePlus',
  [MileageProgram.DELTA_SKYMILES]: 'Delta SkyMiles',
  [MileageProgram.FLYING_BLUE]: 'Flying Blue',
  [MileageProgram.BRITISH_AIRWAYS_AVIOS]: 'British Airways Executive Club (Avios)',
  [MileageProgram.IBERIA_PLUS]: 'Iberia Plus',
  [MileageProgram.ALASKA_MILEAGE_PLAN]: 'Alaska Airlines Mileage Plan',
  [MileageProgram.AIR_CANADA_AEROPLAN]: 'Air Canada Aeroplan',
  [MileageProgram.EMIRATES_SKYWARDS]: 'Emirates Skywards',
  [MileageProgram.QATAR_PRIVILEGE_CLUB]: 'Qatar Airways Privilege Club (Avios)',
  [MileageProgram.TURKISH_MILESSMILES]: 'Turkish Airlines Miles&Smiles',
  [MileageProgram.AVIANCA_LIFEMILES]: 'Avianca LifeMiles',
  [MileageProgram.COPA_CONNECTMILES]: 'Copa Airlines ConnectMiles',
  [MileageProgram.ETIHAD_GUEST]: 'Etihad Guest',
  [MileageProgram.SINGAPORE_KRISFLYER]: 'Singapore Airlines KrisFlyer',
  [MileageProgram.LUFTHANSA_MILES_MORE]: 'Lufthansa Miles & More',
  [MileageProgram.QANTAS_FREQUENT_FLYER]: 'Qantas Frequent Flyer',
  [MileageProgram.CATHAY_ASIA_MILES]: 'Cathay Pacific Asia Miles',
  [MileageProgram.ANA_MILEAGE_CLUB]: 'ANA Mileage Club',
  [MileageProgram.JAL_MILEAGE_BANK]: 'Japan Airlines JAL Mileage Bank',
  [MileageProgram.HAWAIIANMILES]: 'HawaiianMiles',
};

/**
 * Payment method display names
 */
export const PAYMENT_METHOD_LABELS: Record<PaymentMethod, string> = {
  [PaymentMethod.PIX]: 'PIX',
  [PaymentMethod.CARD_1X]: 'Cartão 1x',
  [PaymentMethod.CARD_INSTALLMENTS]: 'Cartão Parcelado',
};
